import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { availableLanguages } from "@/i18n";

interface URLState {
  search: string;
  language: string;
}

interface UseURLStateReturn {
  urlState: URLState;
  updateURL: (search: string, language: string) => void;
  initializeFromURL: () => URLState;
}

// Debounce function
const debounce = <T extends (...args: any[]) => void>(func: T, delay: number): T => {
  let timeoutId: NodeJS.Timeout;

  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
};

export const useURLState = (): UseURLStateReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current URL state
  const urlState = useMemo((): URLState => {
    const search = searchParams.get("search") || "";
    const language = searchParams.get("language") || "";

    // Validate language code
    const validLanguage = availableLanguages.some((lang) => lang.code === language) ? language : "";

    // Safely decode URI component
    let decodedSearch = search;

    try {
      decodedSearch = decodeURIComponent(search);
    } catch {
      // If decoding fails, use the original string
      decodedSearch = search;
    }

    return {
      search: decodedSearch,
      language: validLanguage,
    };
  }, [searchParams]);

  // Debounced URL update function
  // TODO: Pass an inline function to useCallback instead of wrapping the debounce result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((search: string, language: string) => {
      const params = new URLSearchParams();

      // Only add params if they have values
      if (search.trim()) {
        params.set("search", encodeURIComponent(search.trim()));
      }
      if (language && language !== "") {
        params.set("language", language);
      }

      // Replace current history entry to avoid cluttering browser history
      setSearchParams(params, { replace: true });
    }, 500),
    [setSearchParams],
  );

  // Update URL with new search and language values
  const updateURL = useCallback(
    (search: string, language: string) => {
      debouncedUpdate(search, language);
    },
    [debouncedUpdate],
  );

  // Initialize state from URL (for initial page load)
  const initializeFromURL = useCallback((): URLState => {
    const search = searchParams.get("search") || "";
    const language = searchParams.get("language") || "";

    // Validate language code
    const validLanguage = availableLanguages.some((lang) => lang.code === language) ? language : "";

    // Safely decode URI component
    let decodedSearch = search;

    try {
      decodedSearch = decodeURIComponent(search);
    } catch {
      // If decoding fails, use the original string
      decodedSearch = search;
    }

    return {
      search: decodedSearch,
      language: validLanguage,
    };
  }, [searchParams]);

  return {
    urlState,
    updateURL,
    initializeFromURL,
  };
};
